import Dexie, { type Table } from 'dexie';
import { createMirrorMiddleware, type MirrorObserver } from '@/data/mirror/mirror-middleware';
import type {
  Attachment,
  BackupHistory,
  BoardColumn,
  BoardPlacement,
  CalendarEvent,
  CalendarReminder,
  Classification,
  Contact,
  FollowUpLog,
  MatrixPlacement,
  Note,
  NoteImage,
  AppNotification,
  RecurrenceRule,
  Settings,
  SupervisorReminder,
  Task,
  TaskContact,
  Uuid,
} from './index';

export const DATABASE_NAME = 'gentletask';

/**
 * data-model.md 9.1 的 schema。
 * `&taskId` 是唯一索引，直接在資料庫層強制 P1 與四象限的一對一約束。
 */
export class GentleTaskDatabase extends Dexie {
  tasks!: Table<Task, Uuid>;
  taskContacts!: Table<TaskContact, Uuid>;
  taskTypes!: Table<Classification, Uuid>;
  sourceTypes!: Table<Classification, Uuid>;
  tags!: Table<Classification, Uuid>;
  recurrenceRules!: Table<RecurrenceRule, Uuid>;
  contacts!: Table<Contact, Uuid>;
  contactTypes!: Table<Classification, Uuid>;
  relationships!: Table<Classification, Uuid>;
  followUpLogs!: Table<FollowUpLog, Uuid>;
  supervisorReminders!: Table<SupervisorReminder, Uuid>;
  boardColumns!: Table<BoardColumn, Uuid>;
  boardPlacements!: Table<BoardPlacement, Uuid>;
  matrixPlacements!: Table<MatrixPlacement, Uuid>;
  calendarEvents!: Table<CalendarEvent, Uuid>;
  calendarReminders!: Table<CalendarReminder, Uuid>;
  attachments!: Table<Attachment, Uuid>;
  notifications!: Table<AppNotification, Uuid>;
  settings!: Table<Settings, string>;
  backupHistory!: Table<BackupHistory, Uuid>;
  notes!: Table<Note, Uuid>;
  noteImages!: Table<NoteImage, Uuid>;

  /**
   * SQLite 鏡像的觀察者（desktop-app.md 6.1）。
   *
   * middleware 必須在 `open()` 之前註冊，所以在建構子就掛上去，
   * 但真正的觀察者由 `installMirror()` 之後才放進來——桌面版才有，網頁版永遠是 null。
   * 這樣資料層不必知道 Tauri 的存在，也不會有「太晚註冊所以攔不到」的時序問題。
   */
  mirrorObserver: MirrorObserver | null = null;

  constructor(name: string = DATABASE_NAME) {
    super(name);

    this.version(1).stores({
      tasks:
        'id, number, kind, status, priority, dueDate, taskTypeId, sourceTypeId, linkedTaskId, ' +
        'recurrenceRuleId, completedAt, cancelledAt, archivedAt, removedFromBoardAt, deletedAt, ' +
        'updatedAt, *tagIds, *stickers',
      taskContacts: 'id, taskId, contactId, roleInTask, [taskId+contactId]',
      taskTypes: 'id, order, disabledAt',
      sourceTypes: 'id, order, disabledAt',
      tags: 'id, order, disabledAt',
      recurrenceRules: 'id, frequency',
      contacts: 'id, name, contactTypeId, relationshipId, organization, deletedAt, *tagIds',
      contactTypes: 'id, order, disabledAt',
      relationships: 'id, order, disabledAt',
      followUpLogs:
        'id, taskId, occurredAtLocal, nextFollowUpAtLocal, supervisorReminderId, deletedAt, ' +
        '*contactIds',
      supervisorReminders:
        'id, taskId, supervisorContactId, remindAtLocal, state, snoozedUntilLocal',
      boardColumns: 'id, order, kind, archivedAt',
      boardPlacements: 'id, &taskId, columnId, [columnId+order]',
      matrixPlacements: 'id, &taskId, quadrant, [quadrant+order]',
      calendarEvents: 'id, taskId, startAtLocal, endAtLocal, recurrence, deletedAt',
      calendarReminders: 'id, eventId',
      attachments: 'id, ownerType, ownerId, sha256, [ownerType+ownerId]',
      notifications:
        'id, sourceType, sourceId, taskId, eventId, dueAtLocal, state, snoozedUntilLocal',
      settings: 'id',
      backupHistory: 'id, scope, exportedAt',
    });

    // 版本 2：隨手筆記本（desktop-app.md 10.7）。
    // 只是新增一張表，既有資料不需要 upgrade function。
    this.version(2).stores({
      notes: 'id, updatedAt, deletedAt',
    });

    this.version(3).stores({
      noteImages: 'id, noteId, createdAt',
    });

    // 版本 4：筆記的專屬標題與使用者排序。舊筆記沒有 sortOrder 時視為未排序，
    // repository 仍會依原本的更新時間穩定排序，直到使用者第一次調整位置。
    this.version(4).stores({
      notes: 'id, sortOrder, updatedAt, deletedAt',
    });

    this.use(
      createMirrorMiddleware({
        onDirty: (keys) => this.mirrorObserver?.onDirty(keys),
        onBulkClear: () => this.mirrorObserver?.onBulkClear(),
      }),
    );
  }

  /** 匯入的 replace-all 與清除資料流程共用；在單一交易內清空所有資料表。 */
  async clearAllTables(): Promise<void> {
    const tables = this.tables;
    await this.transaction('rw', tables, async () => {
      await Promise.all(tables.map((table) => table.clear()));
    });
  }
}

export const db = new GentleTaskDatabase();

/** 刪除整個資料庫；`/backup` 的清除全部資料使用（data-model 12.2）。 */
export async function deleteDatabase(database: GentleTaskDatabase = db): Promise<void> {
  database.close();
  await database.delete();
}
